import {Directive, HostBinding, Input, Optional, SkipSelf} from '@angular/core';

/**
 * Shim liviano que reemplaza el subconjunto de @angular/flex-layout usado en el
 * proyecto (fxLayout, fxLayoutAlign, fxLayoutGap, fxFlex, fxFlexAlign,
 * fxFlexOffset) con directivas Angular planas que aplican CSS flexbox
 * equivalente. Sin variantes responsive (el proyecto no las usa) y sin
 * dependencia externa, así deja de bloquear el upgrade de Angular.
 */

function justify(main: string): string {
  switch (main) {
    case 'start': return 'flex-start';
    case 'end': return 'flex-end';
    case 'center': return 'center';
    case 'space-between': return 'space-between';
    case 'space-around': return 'space-around';
    case 'space-evenly': return 'space-evenly';
    case 'stretch': return 'stretch';
    default: return 'flex-start';
  }
}

function align(cross: string): string {
  switch (cross) {
    case 'start': return 'flex-start';
    case 'end': return 'flex-end';
    case 'center': return 'center';
    case 'baseline': return 'baseline';
    case 'stretch': return 'stretch';
    default: return 'stretch';
  }
}

@Directive({
    selector: '[fxLayout]',
    standalone: false
})
export class FxLayoutDirective {
  @Input() fxLayout = 'row';

  @HostBinding('style.display') display = 'flex';
  @HostBinding('style.box-sizing') box = 'border-box';

  @HostBinding('style.flex-direction') get direction(): string {
    return (this.fxLayout || '').includes('column') ? 'column' : 'row';
  }

  @HostBinding('style.flex-wrap') get wrap(): string {
    return (this.fxLayout || '').includes('wrap') ? 'wrap' : 'nowrap';
  }

  get isColumn(): boolean {
    return this.direction === 'column';
  }
}

@Directive({
    selector: '[fxLayoutAlign]',
    standalone: false
})
export class FxLayoutAlignDirective {
  @Input() fxLayoutAlign = 'start stretch';

  @HostBinding('style.display') display = 'flex';

  private get parts(): string[] {
    return (this.fxLayoutAlign || '').trim().split(/\s+/);
  }

  @HostBinding('style.justify-content') get justifyContent(): string {
    return justify(this.parts[0]);
  }

  @HostBinding('style.align-items') get alignItems(): string {
    return align(this.parts[1]);
  }

  @HostBinding('style.align-content') get alignContent(): string {
    return align(this.parts[1]);
  }
}

@Directive({
    selector: '[fxLayoutGap]',
    standalone: false
})
export class FxLayoutGapDirective {
  @Input() fxLayoutGap = '0';

  @HostBinding('style.gap') get gap(): string {
    return this.fxLayoutGap || '0';
  }
}

@Directive({
    selector: '[fxFlex]',
    standalone: false
})
export class FxFlexDirective {
  @Input() fxFlex = '';

  @HostBinding('style.box-sizing') box = 'border-box';

  constructor(@Optional() @SkipSelf() private container: FxLayoutDirective) {
  }

  private get value(): string {
    return (this.fxFlex == null ? '' : String(this.fxFlex)).trim();
  }

  private get basis(): string {
    const v = this.value;
    if (!v) {
      return '0%';
    }
    return /^\d+$/.test(v) ? `${v}%` : v; // número puro => porcentaje; si trae unidad, tal cual
  }

  @HostBinding('style.flex') get flex(): string {
    return `1 1 ${this.basis}`;
  }

  @HostBinding('style.max-width') get maxWidth(): string {
    return (!this.value || this.container?.isColumn) ? null : this.basis;
  }

  @HostBinding('style.max-height') get maxHeight(): string {
    return (this.value && this.container?.isColumn) ? this.basis : null;
  }
}

@Directive({
    selector: '[fxFlexAlign]',
    standalone: false
})
export class FxFlexAlignDirective {
  @Input() fxFlexAlign = 'auto';

  @HostBinding('style.align-self') get alignSelf(): string {
    const v = this.fxFlexAlign;
    return v === 'auto' ? 'auto' : align(v);
  }
}

@Directive({
    selector: '[fxFlexOffset]',
    standalone: false
})
export class FxFlexOffsetDirective {
  @Input() fxFlexOffset = '0';

  constructor(@Optional() @SkipSelf() private container: FxLayoutDirective) {
  }

  private get offset(): string {
    const v = (this.fxFlexOffset == null ? '' : String(this.fxFlexOffset)).trim();
    if (!v) {
      return '0';
    }
    return /^\d+$/.test(v) ? `${v}%` : v;
  }

  @HostBinding('style.margin-left') get marginLeft(): string {
    return this.container?.isColumn ? null : this.offset;
  }

  @HostBinding('style.margin-top') get marginTop(): string {
    return this.container?.isColumn ? this.offset : null;
  }
}

export const FLEX_COMPAT_DIRECTIVES = [
  FxLayoutDirective,
  FxLayoutAlignDirective,
  FxLayoutGapDirective,
  FxFlexDirective,
  FxFlexAlignDirective,
  FxFlexOffsetDirective
];
